import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import BasicInfoForm from '@/components/SignUp/BasicInfoForm/BasicInfoForm';
import SignUpComplete from '@/components/SignUp/SignUpComplete/SignUpComplete';
import { StepContent } from '@/components/SignUp/signupStyles';
import { useSignUpStore } from '@/components/SignUp/SignUpContext/SignUpContext';
import Terms from '@/components/SignUp/Terms/Terms';
import useIsMobile from '@/hooks/useIsMobile';

import * as S from './style';

const STEPS = ['회원약관 동의', '기본정보 입력', '회원가입 완료'] as const;

const SignUpContent = () => {
	const { t } = useTranslation();
	const { updateTerms, terms, currentStep, setStep, currentLastStep, clearData, clearStep } = useSignUpStore();
	const isMobile = useIsMobile(900);

	const lastStepIndex = STEPS.indexOf(currentLastStep as (typeof STEPS)[number]);
	const currentIndex = STEPS.indexOf(currentStep as (typeof STEPS)[number]);

	const getStepState = (index: number): 'upcoming' | 'current' | 'complete' => {
		if (index === currentIndex) return 'current';
		if (index < lastStepIndex || (lastStepIndex === STEPS.length - 1 && index < STEPS.length - 1)) return 'complete';
		if (index <= lastStepIndex) return 'complete';
		return 'upcoming';
	};

	const handleStepClick = (index: number) => {
		if (index <= lastStepIndex && lastStepIndex !== STEPS.length - 1) {
			setStep(STEPS[index]);
		}
	};

	useEffect(
		() => () => {
			clearData();
			clearStep();
		},
		[clearData, clearStep]
	);

	return (
		<S.Page>
			<S.Container>
				<S.PageHeader>
					<S.Eyebrow>Membership</S.Eyebrow>
					<S.Title>{t('회원가입')}</S.Title>
					<S.Subtitle>{t('signup.page.subtitle')}</S.Subtitle>
				</S.PageHeader>

				{!isMobile ? (
					<S.Stepper>
						{STEPS.map((item, index) => {
							const state = getStepState(index);
							const clickable = index <= lastStepIndex && lastStepIndex !== STEPS.length - 1;

							return (
								<S.StepItem
									key={item}
									$state={state}
									$clickable={clickable}
									onClick={() => handleStepClick(index)}
									aria-current={state === 'current' ? 'step' : undefined}
								>
									<S.StepCircle $state={state}>{index + 1}</S.StepCircle>
									<S.StepLabel $state={state}>{t(item)}</S.StepLabel>
								</S.StepItem>
							);
						})}
					</S.Stepper>
				) : (
					<S.StepperMobile>
						{STEPS.map((item, index) => (
							<S.StepDot key={item} $state={getStepState(index)}>
								<span>{index + 1}</span>
							</S.StepDot>
						))}
					</S.StepperMobile>
				)}

				<StepContent>
					{currentStep === STEPS[0] && (
						<Terms
							serviceAgree={terms.serviceAgree ?? false}
							privacyAgree={terms.privacyAgree ?? false}
							handleServiceAgree={(value) => updateTerms({ serviceAgree: value, privacyAgree: terms.privacyAgree })}
							handlePrivacyAgree={(value) => updateTerms({ privacyAgree: value, serviceAgree: terms.serviceAgree })}
						/>
					)}
					{currentStep === STEPS[1] && <BasicInfoForm />}
					{currentStep === STEPS[2] && <SignUpComplete />}
				</StepContent>
			</S.Container>
		</S.Page>
	);
};

const SignUp = () => <SignUpContent />;

export default SignUp;
